/**
 * Barras "Recuperado x Comissao" por dia, semana ou mes, no recorte da tela.
 *
 * Decisoes nao obvias:
 *  1. Sem seletor de agrupamento: a granularidade segue a janela do cabecalho
 *     (`agrupar.ts`) e o titulo diz qual e. Um seletor dentro do cartao seria
 *     um segundo filtro de tempo, o que o guia proibe.
 *  2. Recuperado e serie 1 e comissao e serie 2, NA ORDEM da paleta, iguais
 *     ao grafico da Central: a administracao ve as duas telas no mesmo dia.
 *  3. So conversoes ativas entram: uma barra que incluisse a revertida
 *     mostraria comissao que o Converte Facil nao vai receber. Reembolso
 *     que a politica nao reverte continua na barra (a comissao ficou).
 */

import { BarChart, Footnote, SectionCard, type BarSeries } from '../../../components/ui';
import { formatBRLFromCents } from '../../../lib/format';
import type { Granularidade, SerieAgrupada } from '../agrupar';

export const UNIDADE_LABEL: Readonly<Record<Granularidade, string>> = {
  dia: 'dia',
  semana: 'semana',
  mes: 'mês',
};

const DESCRICAO: Readonly<Record<Granularidade, string>> = {
  dia: 'Valor pago pelos leads recuperados e comissão do Converte Fácil, somados por dia.',
  semana: 'Valor pago pelos leads recuperados e comissão do Converte Fácil, somados por semana (segunda a domingo).',
  mes: 'Valor pago pelos leads recuperados e comissão do Converte Fácil, somados por mês.',
};

export function GraficoRecuperadoComissao({
  serie,
  escopo,
  className = '',
}: {
  readonly serie: SerieAgrupada;
  /** "Todas as empresas" ou o nome da empresa filtrada. */
  readonly escopo: string;
  readonly className?: string;
}): JSX.Element {
  const unidade = UNIDADE_LABEL[serie.granularidade];
  const series: readonly BarSeries[] = [
    { key: 'recuperado', label: 'Recuperado (valor pago)', values: serie.recuperadoCents, tone: 1 },
    { key: 'comissao', label: 'Comissão', values: serie.comissaoCents, tone: 2 },
  ];
  const vazio = serie.baldes.every((b) => b.recuperadoCents === 0 && b.comissaoCents === 0);

  return (
    <SectionCard
      id="recuperado-comissao"
      title={`Recuperado e comissão por ${unidade}`}
      icon="grafico"
      description={`${DESCRICAO[serie.granularidade]} ${escopo}.`}
      className={className}
    >
      <div className="flex flex-col gap-4">
        {vazio ? (
          <p className="py-10 text-center text-sm text-ink-subtle">Nenhuma venda recuperada no período: as barras começam vazias.</p>
        ) : (
          <BarChart
            labels={serie.labels}
            series={series}
            formatValue={formatBRLFromCents}
            height={220}
            title={`Valor recuperado e comissão por ${unidade}, ${escopo}`}
          />
        )}
        <Footnote>
          Só conversões ativas entram nas barras: a revertida sai junto com a comissão; a que teve reembolso mantido continua (ADR-001). Até 14 dias de janela,
          uma barra por dia; até 14 semanas, por semana; acima disso, por mês.
        </Footnote>
      </div>
    </SectionCard>
  );
}
